import { z } from 'zod';
import { OrgRole } from './org.js';
import { SignalTopicKey } from './signal-topics.js';
/**
 * Lenient URL parser for user-typed fields. Most onboarding flows let the
 * user paste a bare domain (`fiops.ai`) or a profile path
 * (`linkedin.com/in/foo`) without the protocol — that's what naturally
 * comes out of an address-bar copy + paste. Stock `z.string().url()`
 * rejects both. This preprocesses the value to prepend `https://` when
 * there's no protocol, then validates against the URL parser, so the
 * stored value is always a normalized full URL.
 *
 * - `fiops.ai` → `https://fiops.ai` → valid
 * - `https://fiops.ai/about` → unchanged → valid
 * - `http://localhost:5180` → unchanged → valid
 * - `` (empty) / `undefined` → returned as-is so `.optional()` works
 * - `not a url at all` → preprocessed to `https://not a url at all` → fails
 *   (correct behavior: garbage stays garbage)
 *
 * Defense-in-depth on the server side means the client doesn't need its
 * own normalizer — the same Zod schema parses both inbound wire payloads
 * and stored docs, so a user who types `fiops.ai` ends up with
 * `https://fiops.ai` in Firestore regardless of where the parse fires.
 */
const lenientUrl = z.preprocess((value) => {
    if (typeof value !== 'string')
        return value;
    const trimmed = value.trim();
    if (trimmed === '')
        return value;
    if (/^https?:\/\//i.test(trimmed))
        return trimmed;
    return `https://${trimmed}`;
}, z.string().url());
/**
 * BusinessContext — per-org capture of "who is this company, where are
 * they playing, and what are they trying to do." One doc per org
 * (`id === orgId` by convention) so the lookup is a single get without
 * a query, and a delete cleanly removes the row when an org is wiped.
 *
 * The shape is the load-bearing input to three downstream features:
 *
 *   1. Goal-aware UI personalization (`/orgs/:o/personalization`)
 *      reads the industry / stage / topPriorities to reorder nav and
 *      decide which scorecard rows to push to primary.
 *
 *   2. External signal ingestion (`externalSignals/*`) filters by
 *      `signalTopics` so a recruiting org and a SaaS org see different
 *      feeds even when the underlying news firehose is shared.
 *
 *   3. Playbook tailoring (`/orgs/:o/playbooks/reseed`) deep-merges
 *      industry-specific section overrides on top of the seven default
 *      templates so a recruiting firm's Sales playbook talks about
 *      "placement stages" instead of "deal stages".
 *
 * Captured during onboarding via the multi-step wizard at
 * `/onboarding/wizard`; admins can revisit + edit at any time. The
 * `onboardingCompleted` flag drives the post-auth redirect: signed-in
 * users with the flag false (or no doc at all) land in the wizard
 * before they hit `/today`.
 *
 * We intentionally keep the enums small + closed. New industries /
 * stages / sizes get added explicitly so the personalization rules
 * keep up. Free-text fields (northStarMetric, topPriorities, goals)
 * give the operator escape hatches without ballooning the type space.
 */
export const BUSINESS_CONTEXTS_COLLECTION = 'businessContexts';
/**
 * Industry vertical. Closed enum — adds require code changes (the
 * personalization rule layer + topic-suggestions catalog need to know).
 * `other` exists as the catch-all for orgs that don't fit any of the
 * named verticals; the personalization layer falls back to defaults.
 */
export const BusinessIndustry = z.enum([
    'b2b-saas',
    'consumer-saas',
    'fintech',
    'healthtech',
    'recruiting',
    'agency',
    'ecommerce',
    'marketplace',
    'developer-tools',
    'professional-services',
    'real-estate',
    'edtech',
    'media',
    'other',
]);
/**
 * Funding / maturity stage. Drives runway emphasis (series-a / series-b
 * with a "fundraise" priority bumps the runway scorecard row to primary)
 * and Phase-5 LLM prompts (a profitable bootstrapped company gets a
 * different planning prompt than a series-a burn-rate company).
 */
export const BusinessStage = z.enum([
    'pre-seed',
    'seed',
    'series-a',
    'series-b',
    'series-c-plus',
    'profitable',
    'bootstrapped',
]);
/**
 * Org headcount band. Aligns with the LeanOpx pricing tiers
 * (Seedling 5–25 / Operator 25–75 / Compound 75–150) plus a 150+
 * upper band for orgs we'd refer up to a custom plan. Single source
 * of truth for "how big is this company" across UI + AI prompts.
 */
export const BusinessSize = z.enum(['5-25', '25-75', '75-150', '150-plus']);
/**
 * Goal horizon. Mirrors the EOS planning cadence (quarterly Rocks /
 * annual plan / 3-year picture / 10-year target) so a goal captured
 * here can later be promoted into the matching V/TO + Rocks slot
 * without a remap layer.
 */
export const GoalHorizon = z.enum(['quarterly', 'annual', 'three-year', 'ten-year']);
/**
 * 2-letter USPS state code (always uppercase). Validates the
 * `BusinessGovernance.formationState` field and each entry in the
 * comma-separated `employeeStates` list. Producers (the agent-led
 * wizard's submit handler) normalize input to this shape before
 * sending; this regex is the runtime guard against a hand-edit slip.
 */
const UspsStateCode = z
    .string()
    .regex(/^[A-Z]{2}$/, 'must be a 2-letter USPS state code (e.g. "DE", "CA")');
/**
 * Comma-separated list of 2-letter USPS state codes. Persisted as a
 * single string because the v0 governance timeline UI renders the raw
 * list inline; consumers split on `, ` at read time. The `superRefine`
 * validates every comma-separated token rather than the whole
 * concatenation so a typo'd "CA, NY, foo" surfaces precisely.
 */
const UspsStateCodeList = z
    .string()
    .max(400, 'must be at most 400 characters')
    .superRefine((val, ctx) => {
    if (val.length === 0)
        return;
    for (const token of val.split(',')) {
        const code = token.trim();
        if (!code)
            continue;
        if (!/^[A-Z]{2}$/.test(code)) {
            ctx.addIssue({
                code: 'custom',
                message: `'${code}' is not a 2-letter USPS state code (expected uppercase like "DE")`,
            });
        }
    }
});
/**
 * Governance snapshot — captured during the v6 agent-led onboarding's
 * step 5 so the Governance timeline (filings, board cadence, multi-state
 * HR triggers) can render real items on day 1. All fields optional —
 * the agent-led wizard can persist partial data, and users coming
 * through the form-first wizard skip this surface entirely (they edit
 * later in Settings → Governance).
 *
 * Distinct from the broader Governance feature surface (which models
 * filings + board packages + provider marketplace as separate
 * collections) — this is just the seed data the user volunteers at
 * onboarding time.
 *
 * Normalization contract — `formationState` and `employeeStates` MUST
 * arrive in canonical 2-letter uppercase USPS form. The agent-led
 * wizard's submit handler runs `normalizeUspsState` /
 * `normalizeUspsStateList` over the raw user input; this schema
 * rejects anything not already in canonical form so a hand-rolled
 * caller can't smuggle "de" or "Delaware" past the boundary.
 */
export const BusinessGovernance = z.object({
    /** "Delaware C-Corp", "California LLC", "S-Corp", etc. Free-text so
     *  we cover the long tail without an enum that won't keep up. */
    entityType: z.string().max(120).optional(),
    /** USPS state code of incorporation/formation — "DE", "CA", "NY", etc.
     *  Must be the canonical 2-letter uppercase form. */
    formationState: UspsStateCode.optional(),
    /** Fiscal year-end — "Dec 31" / "Jun 30" / "Mar 31" — drives the
     *  governance timeline's tax filing dates. */
    fiscalYearEnd: z.string().max(40).optional(),
    /** Board cadence — "Quarterly", "Monthly", "Twice yearly", "Annual". */
    boardCadence: z.string().max(40).optional(),
    /** Last funding round — free-text "Series A · $12M · Q3 2025". */
    lastFundingRound: z.string().max(200).optional(),
    /** Board size — "5 seats · 2 investor / 2 founder / 1 indep". */
    boardSize: z.string().max(200).optional(),
    /** States with W-2 employees — drives multi-state HR triggers
     *  (unemployment, workers comp filings). Comma-separated 2-letter
     *  USPS state codes ("CA, NY, WA"); we parse to array at render time. */
    employeeStates: UspsStateCodeList.optional(),
});
/**
 * A captured business goal. `goal` is required free-text (always); the
 * `metric` + `target` pair is optional structured data so operators who
 * think in numbers can attach them and the AI synthesis layer can
 * reason about progress later. `status` stays as 'committed' once the
 * wizard is done — 'achieved' / 'dropped' transitions land via the
 * (Phase-5) goal-tracking surface.
 */
export const BusinessGoal = z.object({
    id: z.string().min(1),
    horizon: GoalHorizon,
    /** Free-text — e.g. "Hit $3M ARR" or "Land 10 design partners". */
    goal: z.string().min(1),
    /** Optional structured metric — e.g. "ARR" or "Active orgs". */
    metric: z.string().optional(),
    /** Optional target value — e.g. "$3M" or "100". */
    target: z.string().optional(),
    status: z.enum(['draft', 'committed', 'achieved', 'dropped']).default('committed'),
});
/**
 * BusinessContext entity. Every field carries default array values so
 * a partial seed (or a v0 wizard that skips later steps) parses cleanly
 * — only the strict-required scalars (industry / stage / size /
 * northStarMetric / companyName / companyDescription) must be present.
 * The store layer enforces the `id === orgId` invariant at write time.
 */
export const BusinessContext = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    /**
     * Display name for the company. Captured on the wizard's identity step;
     * surfaces as the entity name on the seeded company dossier + welcome
     * note + every personalized briefing line.
     */
    companyName: z.string().min(1),
    /**
     * Public homepage URL. Drives the company-enrichment Gemini call which
     * pre-fills industry / stage / size / description / customer. Uses
     * `lenientUrl` so a bare-domain paste like `fiops.ai` is normalized to
     * `https://fiops.ai` before storage.
     */
    companyUrl: lenientUrl.optional(),
    /**
     * One-line "what we do." Personalization layer + welcome-note + dossier
     * caption all read from this.
     */
    companyDescription: z.string().min(1),
    /** Optional "who we serve" — feeds personalization + welcome note tone. */
    customerDescription: z.string().optional(),
    /** Free-text title (CEO, COO, Head of Product, …). */
    userRole: z.string().optional(),
    /** Public LinkedIn profile URL — passive link + optional disambiguator for person enrichment. Uses `lenientUrl` so a path-only paste like `linkedin.com/in/foo` is normalized to `https://linkedin.com/in/foo` before storage. */
    userLinkedinUrl: lenientUrl.optional(),
    /** Background summary — enriched from Gemini-grounded web search or hand-entered. */
    userBackgroundSummary: z.string().optional(),
    /** "What's most on your mind this quarter?" — surfaces in welcome note. */
    currentFocus: z.string().optional(),
    industry: BusinessIndustry,
    /**
     * Populated when `industry === 'other'`. Free-text capture of the user's
     * write-in. Stored alongside the enum so the cache key stays stable; the
     * personalization LLM prompt receives both, and quarterly we promote
     * popular write-ins into the typed enum.
     */
    industryOther: z.string().optional(),
    /**
     * Last enrichment guess for industry, if any. Kept for the post-hoc
     * enum-promotion review — when many users override "Other" we have the
     * raw model guess to compare against.
     */
    inferredIndustry: BusinessIndustry.optional(),
    /**
     * Free-text industry tags — refines the BusinessIndustry enum with
     * operator-supplied detail. Examples: 'recruiting/tech',
     * 'b2b-saas/devtools', 'fintech/cards'. Used by the topic-suggestions
     * heuristic and (Phase 5) by the LLM personalization prompt.
     */
    industryTags: z.array(z.string()).default([]),
    /**
     * Region tags. Curated common regions: 'us-bayarea', 'us-nyc',
     * 'us-other', 'eu-london', 'eu-other', 'apac-singapore',
     * 'apac-tokyo', 'apac-other', 'remote-first'. Drives region-keyed
     * signal topics ('eu-ai-act-updates', 'us-state-privacy-laws').
     */
    region: z.array(z.string()).default([]),
    /** Companion write-ins for "Other" region rows. */
    regionOther: z.array(z.string()).default([]),
    stage: BusinessStage,
    /** Companion write-in when `stage === 'series-c-plus'` doesn't fit. */
    stageOther: z.string().optional(),
    size: BusinessSize,
    /** Companion write-in when none of the four bands fit. */
    sizeOther: z.string().optional(),
    /**
     * The single metric that defines whether we're winning. Free-text so
     * a fintech can say "Net new ARR" while a marketplace says "GMV
     * processed" without us forcing them into a fixed taxonomy. Phase 5
     * AI prompts treat this as the primary callout.
     */
    northStarMetric: z.string().min(1),
    /**
     * 3–5 free-text rows; the Today briefing surfaces these as the
     * "what we care about right now" anchor. Personalization rules
     * scan these for keywords ('fundraise', 'churn', 'hiring') to
     * reorder nav.
     */
    topPriorities: z.array(z.string().min(1)).default([]),
    goals: z.array(BusinessGoal).default([]),
    /**
     * Subscribed signal topics. Each string matches a key in
     * `SIGNAL_TOPICS` (see signal-topics.ts). The external-signal
     * connector pipeline only emits signals whose topic is in this set.
     */
    signalTopics: z.array(SignalTopicKey).default([]),
    /** Free-text "Other" signal topic write-ins. */
    signalTopicsOther: z.array(z.string()).default([]),
    /**
     * Governance snapshot captured during the v6 agent-led onboarding's
     * step 5. Optional — form-first wizard users skip this surface and
     * edit later in Settings → Governance.
     */
    governance: BusinessGovernance.optional(),
    /**
     * Set true when the wizard's confirm step submits. Drives the
     * post-auth redirect: false / missing → `/onboarding/wizard`.
     */
    onboardingCompleted: z.boolean().default(false),
    createdAt: z.string(),
    updatedAt: z.string(),
    updatedByUid: z.string().min(1),
});
/**
 * Body for `PUT /api/orgs/:o/business-context`. Every field optional —
 * the route is upsert (creates the doc if missing). The wizard's
 * confirm step submits the full snapshot in a single PUT; later admin
 * edits PATCH-shaped semantically by sending only the changed fields.
 *
 * Cross-field refinements at the store layer (not on this schema): when
 * `industry === 'other'`, `industryOther` must be present + non-empty on
 * the merged doc post-upsert. Same pattern for stage/size if their
 * companion `*Other` fields end up being needed.
 */
export const UpdateBusinessContextRequest = z.object({
    companyName: z.string().min(1).optional(),
    companyUrl: lenientUrl.optional(),
    companyDescription: z.string().min(1).optional(),
    customerDescription: z.string().optional(),
    userRole: z.string().optional(),
    userLinkedinUrl: lenientUrl.optional(),
    userBackgroundSummary: z.string().optional(),
    currentFocus: z.string().optional(),
    industry: BusinessIndustry.optional(),
    industryOther: z.string().optional(),
    inferredIndustry: BusinessIndustry.optional(),
    industryTags: z.array(z.string()).optional(),
    region: z.array(z.string()).optional(),
    regionOther: z.array(z.string()).optional(),
    stage: BusinessStage.optional(),
    stageOther: z.string().optional(),
    size: BusinessSize.optional(),
    sizeOther: z.string().optional(),
    northStarMetric: z.string().min(1).optional(),
    topPriorities: z.array(z.string().min(1)).optional(),
    goals: z.array(BusinessGoal).optional(),
    signalTopics: z.array(SignalTopicKey).optional(),
    signalTopicsOther: z.array(z.string()).optional(),
    /**
     * Captured invites for the team-invite step. Server-side seeder mints
     * one invite per row via the existing invite-mint route after the
     * BusinessContext upsert returns 200.
     */
    invites: z
        .array(z.object({
        email: z.string().email(),
        role: OrgRole,
    }))
        .optional(),
    /** Governance snapshot — see BusinessGovernance docs. Optional; only
     *  the v6 agent-led wizard step 5 populates this. */
    governance: BusinessGovernance.optional(),
    onboardingCompleted: z.boolean().optional(),
});
